import React, { useState } from 'react';
import { Shift } from '../interfaces';
import { Property } from '../Toolbar';
import { formatPrice } from '../utils/formatPrice';
import XReportPopup from '../components/XReportPopup';
import { Icon } from '../ui/icons';
import { PIN_LENGTH, PinAuthority, verifyPinAuthority } from '../services/StaffAuthService';
import './ReviewVariance.css';

/**
 * The authority a discrepancy needs before the drawer can be signed off. Whoever
 * taps their PIN must hold it, and their name goes on the shift.
 */
const APPROVAL_PERMISSION = 'pos-app.shift.end-day';

interface ReviewVarianceProps {
    shift: Shift | null;
    property: Property | null;
    actualCashCount: number;
    onNext: (varianceNotes: string, approvedBy: string) => void;
    onCancel: () => void;
}

const ReviewVariance: React.FC<ReviewVarianceProps> = ({
    shift,
    property,
    actualCashCount,
    onNext,
    onCancel,
}) => {
    const [varianceReason, setVarianceReason] = useState('');
    const [managerPin, setManagerPin] = useState('');
    const [notes, setNotes] = useState('');
    const [isXReportOpen, setIsXReportOpen] = useState(false);
    const [pinError, setPinError] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [approver, setApprover] = useState<PinAuthority | null>(null);

    const expectedCash = shift?.expectedCash ?? 0;
    const variance = actualCashCount - expectedCash;
    const hasVariance = Math.abs(variance) > 0.01;

    const formatCurrency = (value: number) => {
        return formatPrice(value);
    };

    const formatVariance = (value: number) => {
        return formatPrice(value);
    };

    const currentSales = shift?.totalRevenue ?? 0;

    /**
     * Check the PIN against whoever it belongs to, not against the person closing
     * the drawer. A manager leans over, taps four digits, and their name is what
     * gets recorded — which is the entire point of an approval.
     */
    const verifyApproval = async (pin: string) => {
        setIsValidating(true);
        setPinError('');

        const result = await verifyPinAuthority(pin, APPROVAL_PERMISSION);
        setIsValidating(false);

        if (result.ok) {
            setApprover(result.approver);
            return;
        }
        setPinError(result.message);
        setManagerPin('');
    };

    const handlePinChange = (raw: string) => {
        const digits = raw.replace(/\D/g, '').slice(0, PIN_LENGTH);
        setManagerPin(digits);
        setPinError('');
        if (digits.length === PIN_LENGTH) verifyApproval(digits);
    };

    const clearApproval = () => {
        setApprover(null);
        setManagerPin('');
        setPinError('');
    };

    const handleNext = () => {
        if (hasVariance && !approver) {
            setPinError('A manager must approve this discrepancy');
            return;
        }

        const fullNotes = varianceReason ? `Reason: ${varianceReason}\n${notes}` : notes;
        onNext(fullNotes, approver?.displayName || '');
    };

    return (
        <div className="review-variance-page ds-pop">
            <div className="review-variance-container">
                {/* Left Section */}
                <div className="review-variance-main">
                    <h2 className="review-variance-title">Review Variance</h2>

                    {/* Cash Variance Card */}
                    <div className="variance-card">
                        <div className="variance-card-header">
                            <span className="variance-card-title">Cash Variance</span>
                            {hasVariance && (
                                <span className="variance-badge">VARIANCE</span>
                            )}
                        </div>
                        <div className="variance-amounts">
                            <div className="variance-amount-item">
                                <span className="variance-label">Expected</span>
                                <span className="variance-value">{formatCurrency(expectedCash)}</span>
                            </div>
                            <div className="variance-amount-item">
                                <span className="variance-label">Counted</span>
                                <span className="variance-value">{formatCurrency(actualCashCount)}</span>
                            </div>
                            <div className="variance-amount-item">
                                <span className="variance-label">Diff</span>
                                <span className={`variance-value ${variance < 0 ? 'negative' : variance > 0 ? 'positive' : ''}`}>
                                    {formatVariance(variance)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Variance Warning */}
                    {hasVariance && (
                        <div className="variance-warning">
                            <div className="variance-warning-header">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div className="variance-warning-text">
                                    <span className="variance-warning-title">Variance Detected</span>
                                    <span className="variance-warning-total">Total: {formatVariance(variance)}.</span>
                                </div>
                            </div>
                            <input
                                type="text"
                                className="variance-reason-input"
                                placeholder="Reason..."
                                value={varianceReason}
                                onChange={(e) => setVarianceReason(e.target.value)}
                            />
                        </div>
                    )}

                    {/* Manager approval — the drawer cannot be signed off without it. */}
                    {hasVariance && (
                        <div className={`manager-override${approver ? ' is-approved' : ''}`}>
                            <div className="manager-override-header">
                                {approver ? <Icon.CircleCheck size={18} /> : <Icon.Lock size={18} />}
                                <span className="manager-override-title">MANAGER APPROVAL</span>
                            </div>

                            {approver ? (
                                <div className="manager-approved">
                                    <div className="manager-approved-who">
                                        <span className="manager-approved-name">{approver.displayName}</span>
                                        <span className="manager-approved-role">
                                            {[approver.roleName, 'approved this discrepancy'].filter(Boolean).join(' · ')}
                                        </span>
                                    </div>
                                    <button type="button" className="manager-approved-change" onClick={clearApproval}>
                                        Change
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <p className="manager-override-hint">
                                        A manager has to approve a cash discrepancy before the shift
                                        closes. Their PIN identifies them, and their name is recorded
                                        against this shift.
                                    </p>
                                    <input
                                        type="password"
                                        className="manager-pin-input"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder={`Manager PIN (${PIN_LENGTH} digits)`}
                                        value={managerPin}
                                        onChange={(e) => handlePinChange(e.target.value)}
                                        disabled={isValidating}
                                    />
                                    {isValidating && <span className="pin-checking">Checking…</span>}
                                </>
                            )}

                            {pinError && <span className="pin-error">{pinError}</span>}
                        </div>
                    )}

                    {/* Notes Section */}
                    <div className="notes-section">
                        <label className="notes-label">NOTES</label>
                        <textarea
                            className="notes-textarea"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={5}
                        />
                    </div>
                </div>

                {/* Right Section */}
                <div className="review-variance-sidebar">
                    <div className="shift-info">
                        <h3 className="shift-number">Z#{shift?.shiftId || '000000'}</h3>
                        <p className="company-name">{property?.displayName}</p>
                    </div>

                    <div className="current-sales">
                        <span>Current Sales:</span>
                        <span className="sales-value">{formatCurrency(currentSales)}</span>
                    </div>

                    <div className="sidebar-actions">
                        <button
                            className="next-deposit-btn"
                            onClick={handleNext}
                            disabled={isValidating || (hasVariance && !approver)}
                            title={hasVariance && !approver ? 'Needs a manager’s approval first' : undefined}
                        >
                            {isValidating ? 'Checking PIN…' : 'Next: Deposit'}
                            {!isValidating && (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </button>

                        <div className="secondary-actions">
                            <button className="secondary-btn xreport-btn" onClick={() => setIsXReportOpen(true)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6 18H4C2.89543 18 2 17.1046 2 16V11C2 9.89543 2.89543 9 4 9H20C21.1046 9 22 9.89543 22 11V16C22 17.1046 21.1046 18 20 18H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                X-Report
                            </button>
                            <button className="secondary-btn cancel" onClick={onCancel}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M21 12H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <XReportPopup
                isOpen={isXReportOpen}
                onClose={() => setIsXReportOpen(false)}
                shift={shift}
                property={property}
            />
        </div>
    );
};

export default ReviewVariance;